import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Trash2, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { errorMessage } from '@/lib/errorMessage';

export default function MatrimoniPanel() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const res = await api.get('/admin/matrimoni-requests');
            setRows(res.data || []);
        } catch (e) { toast.error('Impossibile caricare le richieste'); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const remove = async (id) => {
        if (!window.confirm('Eliminare questa richiesta?')) return;
        try {
            await api.delete(`/admin/matrimoni-requests/${id}`);
            toast.success('Eliminata');
            await load();
        } catch (e) { toast.error(errorMessage(e, 'Errore')); }
    };

    const exportCsv = () => {
        const headers = ['created_at', 'full_name', 'email', 'phone', 'event_date', 'location_ceremony', 'location_reception', 'budget', 'style', 'notes', 'status'];
        const escape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
        const csv = [headers.join(','), ...rows.map((r) => headers.map((h) => escape(r[h])).join(','))].join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = `matrimoni_${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div data-testid="admin-matrimoni-panel">
            <div className="flex items-center justify-between mb-4">
                <div>
                    <h2 className="font-serif text-xl">Richieste matrimoni</h2>
                    <p className="text-xs text-muted-foreground mt-0.5">Richieste ricevute dal form dedicato.</p>
                </div>
                <Button onClick={exportCsv} variant="outline" size="sm" disabled={rows.length === 0}>
                    <Download className="h-3.5 w-3.5 mr-1.5" />Esporta CSV
                </Button>
            </div>
            {loading ? (
                <p className="text-sm text-muted-foreground py-8 text-center"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Caricamento…</p>
            ) : rows.length === 0 ? (
                <p className="rounded-xl border border-dashed border-border bg-muted/40 p-10 text-center text-sm text-muted-foreground">Nessuna richiesta ricevuta.</p>
            ) : (
                <div className="space-y-3">
                    {rows.map((r) => (
                        <div key={r.id} className="rounded-xl border border-border bg-card p-5">
                            <div className="flex flex-wrap items-start justify-between gap-4">
                                <div>
                                    <p className="font-serif text-lg">{r.full_name}</p>
                                    <p className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleString('it-IT')}</p>
                                </div>
                                <div className="text-right text-xs">
                                    {r.email && <a href={`mailto:${r.email}`} className="block hover:text-atelier-sage-ink">{r.email}</a>}
                                    {r.phone && <a href={`tel:${r.phone}`} className="block text-muted-foreground">{r.phone}</a>}
                                </div>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 mt-3 text-sm">
                                <div><span className="eyebrow text-[10px] block">Data matrimonio</span>{r.event_date}</div>
                                {r.budget && <div><span className="eyebrow text-[10px] block">Budget</span>{r.budget}</div>}
                                <div><span className="eyebrow text-[10px] block">Cerimonia</span>{r.location_ceremony}</div>
                                <div><span className="eyebrow text-[10px] block">Ricevimento</span>{r.location_reception}</div>
                                {r.style && <div className="sm:col-span-2"><span className="eyebrow text-[10px] block">Stile</span>{r.style}</div>}
                                {r.notes && <div className="sm:col-span-2"><span className="eyebrow text-[10px] block">Note</span>{r.notes}</div>}
                            </div>
                            <div className="mt-3 pt-3 border-t border-border flex justify-end">
                                <Button variant="ghost" size="sm" onClick={() => remove(r.id)} className="text-destructive h-8"><Trash2 className="h-3.5 w-3.5 mr-1.5" />Elimina</Button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
